import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  Fab,
  Tabs,
  Tab,
  Badge,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Phone,
  Email,
  LocationOn,
  AccountBalance,
  CreditCard,
  Person,
  Business,
  Visibility,
  AccountBalanceWallet,
  TrendingUp,
  TrendingDown,
  Assessment,
  Inventory,
  ShoppingCart,
  Assignment
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import * as goldsmithActions from '../redux/actions/goldsmithActions';

const GoldsmithManagement = () => {
  const dispatch = useDispatch();
  const { goldsmiths, goldsmithsLoading, goldsmithsError } = useSelector(state => state.goldsmith);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedGoldsmith, setSelectedGoldsmith] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create, edit, view
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gstin: '',
    bank_details: '',
    credit_limit: '',
    is_active: true
  });

  useEffect(() => {
    dispatch(goldsmithActions.getGoldsmiths());
  }, [dispatch]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    dispatch(goldsmithActions.getGoldsmiths({ search: e.target.value }));
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    const params = { search: searchTerm };
    if (e.target.value !== 'all') {
      params.is_active = e.target.value === 'active';
    }
    dispatch(goldsmithActions.getGoldsmiths(params));
  };

  const handleOpenDialog = (mode, goldsmith = null) => {
    setDialogMode(mode);
    if (mode === 'edit' || mode === 'view') {
      setFormData({
        name: goldsmith.name || '',
        contact_person: goldsmith.contact_person || '',
        phone: goldsmith.phone || '',
        email: goldsmith.email || '',
        address: goldsmith.address || '',
        gstin: goldsmith.gstin || '',
        bank_details: goldsmith.bank_details || '',
        credit_limit: goldsmith.credit_limit || '',
        is_active: goldsmith.is_active !== undefined ? goldsmith.is_active : true
      });
      setSelectedGoldsmith(goldsmith);
    } else {
      setFormData({
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        gstin: '',
        bank_details: '',
        credit_limit: '',
        is_active: true
      });
      setSelectedGoldsmith(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedGoldsmith(null);
    setCurrentTab(0);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await dispatch(goldsmithActions.createGoldsmith(formData));
        setSnackbar({ open: true, message: 'Goldsmith created successfully!', severity: 'success' });
      } else if (dialogMode === 'edit') {
        await dispatch(goldsmithActions.updateGoldsmith(selectedGoldsmith.id, formData));
        setSnackbar({ open: true, message: 'Goldsmith updated successfully!', severity: 'success' });
      }
      handleCloseDialog();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'An error occurred', severity: 'error' });
    }
  };

  const handleDelete = async (goldsmith) => {
    if (window.confirm(`Are you sure you want to delete ${goldsmith.name}?`)) {
      try {
        await dispatch(goldsmithActions.deleteGoldsmith(goldsmith.id));
        setSnackbar({ open: true, message: 'Goldsmith deleted successfully!', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: error.message || 'An error occurred', severity: 'error' });
      }
    }
  };

  const filteredGoldsmiths = goldsmiths.filter(goldsmith => {
    const matchesSearch = goldsmith.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         goldsmith.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         goldsmith.phone?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && goldsmith.is_active) ||
                         (statusFilter === 'inactive' && !goldsmith.is_active);
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    return status ? 'success' : 'error';
  };

  const getStatusLabel = (status) => {
    return status ? 'Active' : 'Inactive';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            <Business sx={{ mr: 2, verticalAlign: 'middle' }} />
            Goldsmith Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your goldsmiths and suppliers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog('create')}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
              boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
            }
          }}
        >
          Add Goldsmith
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search goldsmiths..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  icon={<Business />}
                  label={`${filteredGoldsmiths.length} Goldsmiths`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Goldsmiths Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Goldsmith</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Credit Limit</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {goldsmithsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredGoldsmiths.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No goldsmiths found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredGoldsmiths.map((goldsmith) => (
                  <TableRow key={goldsmith.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: goldsmith.is_active ? '#4caf50' : '#f44336',
                            width: 40,
                            height: 40
                          }}
                        >
                          <Business />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {goldsmith.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {goldsmith.contact_person}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {goldsmith.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">{goldsmith.phone}</Typography>
                          </Box>
                        )}
                        {goldsmith.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">{goldsmith.email}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(goldsmith.credit_limit)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={goldsmith.current_balance >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(goldsmith.current_balance)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(goldsmith.is_active)}
                        color={getStatusColor(goldsmith.is_active)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('view', goldsmith)}
                            color="info"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', goldsmith)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(goldsmith)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Goldsmith Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          p: 3
        }}>
          <Typography variant="h5" fontWeight="bold">
            {dialogMode === 'create' && 'Add New Goldsmith'}
            {dialogMode === 'edit' && 'Edit Goldsmith'}
            {dialogMode === 'view' && 'Goldsmith Details'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Goldsmith Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                required
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Person"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Email /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                multiline
                rows={3}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LocationOn /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="GSTIN"
                name="gstin"
                value={formData.gstin}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Credit Limit"
                name="credit_limit"
                type="number"
                value={formData.credit_limit}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Details"
                name="bank_details"
                value={formData.bank_details}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                multiline
                rows={2}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AccountBalance /></InputAdornment>
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        {dialogMode !== 'view' && (
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ borderRadius: 2, px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 4,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                }
              }}
            >
              {dialogMode === 'create' ? 'Create Goldsmith' : 'Update Goldsmith'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GoldsmithManagement;
